<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar color="indigo">
            <v-icon dark>
              {{ $t(`Payment.Dialog.View.Toolbar.Icon`) }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t(`Payment.Dialog.View.Toolbar.Title`) }} #{{ data?.TransactionId }} (#{{ data?.Id }})
            </div>

            <div class="text-caption grey--text">
              {{ terminalName }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="closeDialog"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text
          ref="body"
          :style="isDarkMode ? 'background-color: #323232;' : ''"
          class="px-3 py-0"
        >
          <v-timeline
            align-top
            dense
          >
            <template v-for="(group, key, groupIdx) in itemHistory">
              <v-timeline-item
                :key="`timeline-group-${groupIdx}`"
                class="text-body-1 font-weight-bold pl-2 pb-2"
                hide-dot
              >
                {{ key }}
              </v-timeline-item>

              <template v-for="(itm, index) in group">
                <v-timeline-item
                  :key="`timeline-group-${groupIdx}-item-${index}`"
                  :color="getTransactionType(itm.Type).Color"
                  :icon="getTransactionType(itm.Type).Icon"
                  class="pb-4"
                  fill-dot
                >
                  <v-container
                    class="py-2 px-1"
                    fluid
                  >
                    <v-row dense>
                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :label="itm.Time"
                          :min-width="90"
                          color="grey darken-1"
                          dense
                          icon="mdi-clock"
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :label="$options.filters.currency(data.Amount)"
                          :min-width="100"
                          color="blue-grey"
                          dense
                          icon="credit_card"
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          v-if="getTransactionStatus(itm.Status)"
                          :avatar-color="`${getTransactionStatus(itm.Status).Color} darken-2`"
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex mb-2': $vuetify.breakpoint.xsOnly}"
                          :color="getTransactionStatus(itm.Status).Color"
                          :icon="getTransactionStatus(itm.Status).Icon"
                          :icon-only="$vuetify.breakpoint.smAndUp"
                          :label="getTransactionStatus(itm.Status).Name"
                          :tooltip="getTransactionStatus(itm.Status).Tooltip"
                          dense
                          label-class="text-left text-sm-center"
                        />

                        <status-chip
                          v-if="getTransactionResult(itm.Result)"
                          :avatar-color="`${getTransactionResult(itm.Result).ColorAvatar} darken-2`"
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :color="getTransactionResult(itm.Result).Color"
                          :icon="getTransactionResult(itm.Result).Icon"
                          :label="getTransactionResult(itm.Result).Name"
                          :min-width="115"
                          :tooltip="getTransactionResultTooltip(itm.Result, {user: itm?.User || item?.Item?.User || ''}, itm?.Message || '')"
                          dense
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <user-chip
                          v-if="itm.User || item?.Item?.User"
                          :color="itm.UserColor || item?.Item?.UserColor"
                          :full-name="itm.User || item?.Item?.User"
                          :short-name="itm.UserShortName || item?.Item?.UserShortName"
                          class="d-inline-flex"
                        />
                      </v-col>

                      <v-col
                        v-if="userIsSuperAdmin"
                        :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'"
                        class="grow text-end"
                      >
                        <v-btn
                          :disabled="!itm.Id"
                          :href="getHistoryItemRawDataUrl(itm)"
                          class="px-2"
                          color="brown"
                          dark
                          depressed
                          height="24"
                          small
                          style="min-width: 0;"
                          target="_blank"
                        >
                          <v-icon left>
                            mdi-code-block-braces
                          </v-icon>

                          DATA
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-timeline-item>
              </template>
            </template>
          </v-timeline>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            v-if="userIsSuperAdmin"
            :disabled="!itemData"
            class="px-2"
            color="brown"
            dark
            depressed
            @click="showRawData"
          >
            <v-icon left>
              mdi-code-block-braces
            </v-icon>

            RAW DATA
          </v-btn>

          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="closeDialog"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transaction-raw-data-details-dialog
      v-if="userIsSuperAdmin && itemData"
      :data="data"
      :visible.sync="rawDataDialogVisible"
    />
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import DialogVisible from '@/mixins/dialogVisible'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'
import StatusChip from '@/components/common/StatusChip.vue'
import {sortBy} from '@/lib/helper/helper.js'
import TransactionRawDataDetailsDialog from '@/components/payments/TransactionRawDataDetailsDialog.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'EftPosTransactionDetailsDialog',
  components: {UserChip, TransactionRawDataDetailsDialog, StatusChip},
  mixins    : [Auth, AppData, DialogVisible, TerminalsCommon],
  props     : {
    item: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      rawDataDialogVisible: false
    }
  },
  computed: {
    data() {
      return this.item?.Data || {}
    },

    itemData() {
      return this.data?.Data || null
    },

    itemHistory() {
      const history = this.data?.History || []

      this.data?.HistoryConfirmed?.forEach((item) => {
        if (item?.ConfirmedAt && item?.RevertedAt) {
          history.push(
            {
              Id             : null,
              IntentId       : null,
              EftPaymentsId  : item.EftPaymentsId,
              UpdatedAt      : item?.RevertedAt || '',
              RevertedAt     : item?.RevertedAt || '',
              ConfirmedAt    : item?.ConfirmedAt || '',
              UserConfirmedAt: item.ConfirmedAt || '',
              FailedAt       : null,
              Type           : 'SALE',
              Result         : 'REVERTED_MANUAL',
              Status         : 'COMPLETED',
              Via            : 'user',
              User           : item?.RevertedUser || '',
              UserShortName  : item?.RevertedUserShortName || '',
              UserColor      : item?.RevertedUserColor || ''
            }
          )

          history.push(
            {
              Id             : null,
              IntentId       : null,
              EftPaymentsId  : item.EftPaymentsId,
              UpdatedAt      : item?.ConfirmedAt || '',
              RevertedAt     : item?.RevertedAt || '',
              ConfirmedAt    : item?.ConfirmedAt || '',
              UserConfirmedAt: item.ConfirmedAt || '',
              FailedAt       : null,
              Type           : 'SALE',
              Result         : 'APPROVED_MANUAL',
              Status         : 'COMPLETED',
              Via            : 'user',
              User           : item?.User || '',
              UserShortName  : item?.UserShortName || '',
              UserColor      : item?.UserColor || ''
            }
          )
        } else if (item?.ConfirmedAt && !item?.RevertedAt) {
          history.push(
            {
              Id             : null,
              IntentId       : null,
              EftPaymentsId  : item.EftPaymentsId,
              UpdatedAt      : item?.ConfirmedAt || '',
              RevertedAt     : item?.RevertedAt || '',
              ConfirmedAt    : item?.ConfirmedAt || '',
              UserConfirmedAt: item.ConfirmedAt || '',
              FailedAt       : null,
              Type           : 'SALE',
              Result         : 'APPROVED_MANUAL',
              Status         : 'COMPLETED',
              Via            : 'user',
              User           : item?.User || '',
              UserShortName  : item?.UserShortName || '',
              UserColor      : item?.UserColor || ''
            }
          )
        }

      })

      /*
      if (this.item?.Item?.UserConfirmedAt) history.push(
        {
          Id             : null,
          IntentId       : null,
          EftPaymentsId  : null,
          UpdatedAt      : this.item.Item.UserConfirmedAt,
          UserConfirmedAt: this.item.Item.UserConfirmedAt,
          FailedAt       : null,
          Type           : 'SALE',
          Result         : 'APPROVED',
          Status         : 'COMPLETED',
          Via            : 'user'
        }
      )
       */

      history.forEach((item) => {
        [item.Date, item.Time] = item.UpdatedAt?.split(' ')
      })


      const historyGroupedByDate = Object.groupBy(sortBy(history, 'Time', 'asc'), item => item.Date)

      return historyGroupedByDate
    },

    terminalName() {
      const terminal = this.getTerminal(this.data?.TerminalVendorId, this.data?.TerminalModelId)
      return terminal ? `${this.data?.TerminalNickname} / ${terminal.Manufacturer} - ${terminal.Model}` : ''
    }
  },
  watch: {
    isVisible(val) {
      this.rawDataDialogVisible = false
      if (this.$refs.body) this.$refs.body.scrollTop = 0
    }
  },
  methods: {
    showRawData() {
      this.rawDataDialogVisible = true
    },

    getHistoryItemRawDataUrl(item) {
      const env = import.meta.env.MODE

      return `${env === 'production' ? `https://lapi.getfoodpro.gr/v2/raw/webecr/transaction/${item.Id}/${this.authorizationToken}` : `https://dapi.getfoodpro.gr/v2/raw/webecr/transaction/${item.Id}/${this.authorizationToken}`}`
    }
  }
}

</script>

<style scoped>

</style>
